import {
  Color,
  type Material,
  Mesh,
  MeshBasicMaterial,
  type Object3D,
  type Scene,
  type WebGLRenderer
} from "three";
import type { DeviceModelLoader } from "../models/device-model-library";
import type { ModelAssetLoader } from "../models/model-asset-library";
import { markSharedModelMaterial } from "../models/shared-model-resources";
import { createFactoryLighting, type FactoryLightingRig } from "./factory-lighting";

export type CargoRenderOptions = {
  environmentAndToneMapping: boolean;
  factoryLighting: boolean;
  highDetailBinCargo: boolean;
  pbrMaterials: boolean;
  shadows: boolean;
};

export const defaultCargoRenderOptions: CargoRenderOptions = {
  environmentAndToneMapping: false,
  factoryLighting: false,
  highDetailBinCargo: false,
  pbrMaterials: false,
  shadows: false
};

export const highQualityCargoRenderOptions: CargoRenderOptions = {
  environmentAndToneMapping: true,
  factoryLighting: true,
  highDetailBinCargo: true,
  pbrMaterials: true,
  shadows: true
};

export type CargoRenderContext = FactoryLightingRig & {
  deviceModelLoader: DeviceModelLoader;
  maxCargoPrimitiveTriangles?: number;
  modelAssetLoader: ModelAssetLoader;
  prepareObject: (object: Object3D) => Object3D;
};

const performanceBackground = "#070a0d";
const performanceCargoPrimitiveTriangleLimit = 10_000;

export function createCargoRenderContext(
  options: CargoRenderOptions,
  scene: Scene,
  renderer: WebGLRenderer,
  target: Object3D,
  modelAssetLoader: ModelAssetLoader,
  deviceModelLoader: DeviceModelLoader
): CargoRenderContext {
  scene.background = new Color(performanceBackground);
  const lighting = createFactoryLighting(scene, renderer, target, {
    environmentAndToneMapping: options.environmentAndToneMapping,
    lights: options.factoryLighting,
    shadows: options.shadows
  });
  const prepareObject = (object: Object3D) => {
    if (!options.pbrMaterials) {
      makeObjectUnlit(object);
    }
    setObjectShadows(object, options.shadows);
    return object;
  };

  return {
    ...lighting,
    deviceModelLoader: createDeviceModelLoader(deviceModelLoader, prepareObject),
    maxCargoPrimitiveTriangles: options.highDetailBinCargo
      ? undefined
      : performanceCargoPrimitiveTriangleLimit,
    modelAssetLoader: createModelAssetLoader(modelAssetLoader, prepareObject),
    prepareObject
  };
}

export function areCargoRenderOptionsEqual(left: CargoRenderOptions, right: CargoRenderOptions) {
  return Object.keys(left).every(
    (key) => left[key as keyof CargoRenderOptions] === right[key as keyof CargoRenderOptions]
  );
}

function createModelAssetLoader(
  loader: ModelAssetLoader,
  prepareObject: (object: Object3D) => Object3D
): ModelAssetLoader {
  return {
    async load(asset) {
      return prepareObject(await loader.load(asset));
    }
  };
}

function createDeviceModelLoader(
  loader: DeviceModelLoader,
  prepareObject: (object: Object3D) => Object3D
): DeviceModelLoader {
  return {
    async load(model) {
      return prepareObject(await loader.load(model));
    }
  };
}

function makeObjectUnlit(object: Object3D) {
  object.traverse((child) => {
    if (!(child instanceof Mesh)) {
      return;
    }

    child.material = Array.isArray(child.material)
      ? child.material.map((material) => convertMaterial(material))
      : convertMaterial(child.material);
  });
  return object;
}

function setObjectShadows(object: Object3D, enabled: boolean) {
  object.traverse((child) => {
    if (child instanceof Mesh) {
      child.castShadow = enabled;
      child.receiveShadow = enabled;
    }
  });
}

const sharedUnlitMaterials = new WeakMap<Material, MeshBasicMaterial>();

function convertMaterial(material: Material) {
  if (material instanceof MeshBasicMaterial) {
    return material;
  }

  const cachedMaterial = sharedUnlitMaterials.get(material);
  if (cachedMaterial) {
    return cachedMaterial;
  }

  const unlitMaterial = new MeshBasicMaterial().copy(material as MeshBasicMaterial);
  sharedUnlitMaterials.set(material, unlitMaterial);
  markSharedModelMaterial(unlitMaterial);
  return unlitMaterial;
}
